import { type JSX, lazy, Suspense } from 'react';
import { cx } from '@linaria/core';

import type { OddsFormat } from '@cx-modules/types';

import { useBookmakersAll } from '../../hooks/useBookmakersAll';
import { OddsSorting, useOddsSorting } from '../../hooks/useOddsSorting';
import { useSwitchCellContent } from '../../hooks/useSwitchCellContent';
import { DesktopOnly, MobileOnly } from '../../OddsGrid.style';
import { useSelector } from '../../providers/StoreProvider';
import type { Bet } from '../../types';

import { OddsCellsDefault } from './OddsCellsDefault';
import { OddsActionsWrapper, oddsAreaWrapper, OffersMobileFallback } from './style';

const OddsInfoMobile = lazy(() => import(/* webpackChunkName: "chunk-OddsInfoMobile" */ '../OddsInfo/OddsInfoMobile'));
const OddsCellsBestPrice = lazy(
  () => import(/* webpackChunkName: "chunk-OddsCellsBestPrice" */ './OddsCellsBestPrice'),
);
const OffersMobile = lazy(() => import(/* webpackChunkName: "chunk-OffersMobile" */ './OffersMobile'));
const OddsActionsMobile = lazy(() => import(/* webpackChunkName: "chunk-OddsActionsMobile" */ './OddsActionsMobile'));
interface OddsOffersCellsProps {
  bet: Bet;
  betsWithBestOdds: (Bet & { bestOdds: number; bestOddsFractional: string; bestOddsText: string | number })[];
  oddsFormat: OddsFormat;
}

export const OddsOffersCells = ({ bet, betsWithBestOdds, oddsFormat }: OddsOffersCellsProps): JSX.Element => {
  const bookmakerIds = useBookmakersAll().map(bookmaker => bookmaker.code) as string[];
  const allowOddsClickout = useSelector(store => store.allowOddsClickout);

  const { contentType, filterClicked, filters } = useSwitchCellContent();
  const { sorting } = useOddsSorting();

  const oddsActions = (
    <Suspense fallback={<OddsActionsWrapper />}>
      <OddsActionsMobile filterClicked={filterClicked} filters={filters} />
    </Suspense>
  );

  const offersMobile = (
    <Suspense fallback={<OffersMobileFallback bookmakersNumber={bookmakerIds.length} />}>
      <OffersMobile betId={bet.id} bookmakerIds={bookmakerIds} />
    </Suspense>
  );

  const oddsInfoMobile = (
    <Suspense fallback={<div></div>}>
      <OddsInfoMobile />
    </Suspense>
  );

  const oddsCellsBestPrice = (
    <Suspense fallback={<div></div>}>
      <OddsCellsBestPrice />
    </Suspense>
  );

  return (
    <>
      <DesktopOnly>
        <OddsCellsDefault bet={bet} betsWithBestOdds={betsWithBestOdds} oddsFormat={oddsFormat} />
      </DesktopOnly>

      <MobileOnly>
        <div className={cx(oddsAreaWrapper)} data-testid="odds-wrapper-mobile">
          {allowOddsClickout && oddsActions}
          {contentType === 'odds' && sorting === OddsSorting.Default && (
            <OddsCellsDefault bet={bet} betsWithBestOdds={betsWithBestOdds} oddsFormat={oddsFormat} />
          )}
          {contentType === 'odds' && sorting === OddsSorting.BestPrices && oddsCellsBestPrice}
          {contentType === 'offers' && offersMobile}
          {allowOddsClickout && oddsInfoMobile}
        </div>
      </MobileOnly>
    </>
  );
};
